import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AlertNotificationService {
  private isMuted: boolean = false;
  private audioCtx: any = null;
  private hasRequestedNotificationPermission: boolean = false;

  constructor() {
    this.initAudioContext();
  }

  private initAudioContext() {
    try {
      const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        this.audioCtx = new AudioContextClass();
      }
    } catch (e) {
      console.warn('Web Audio API not supported in this browser environment.', e);
    }
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    return this.isMuted;
  }

  public getMuteState(): boolean {
    return this.isMuted;
  }

  /**
   * Synthesize a crisp industrial chime/beep sound using Web Audio API
   */
  public playAlertSound(severity: 'warning' | 'critical' | 'info' = 'warning') {
    if (this.isMuted) return;

    try {
      if (!this.audioCtx) {
        this.initAudioContext();
      }
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }

      if (!this.audioCtx) return;

      const osc = this.audioCtx.createOscillator();
      const gain = this.audioCtx.createGain();

      osc.connect(gain);
      gain.connect(this.audioCtx.destination);

      const now = this.audioCtx.currentTime;

      if (severity === 'critical') {
        // High-pitched double beep alarm
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(880, now); // A5
        osc.frequency.setValueAtTime(1200, now + 0.15);
        gain.gain.setValueAtTime(0.3, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
        osc.start(now);
        osc.stop(now + 0.4);
      } else if (severity === 'warning') {
        // Medium warning chime
        osc.type = 'sine';
        osc.frequency.setValueAtTime(587.33, now); // D5
        osc.frequency.setValueAtTime(783.99, now + 0.12); // G5
        gain.gain.setValueAtTime(0.25, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
        osc.start(now);
        osc.stop(now + 0.35);
      } else {
        // Soft notification tone
        osc.type = 'sine';
        osc.frequency.setValueAtTime(523.25, now); // C5
        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
        osc.start(now);
        osc.stop(now + 0.25);
      }
    } catch (err) {
      console.warn('Could not play alert sound:', err);
    }
  }

  /**
   * Request native browser notification permission and send toast
   */
  public triggerDesktopNotification(title: string, body: string, severity: 'critical' | 'warning' | 'info' = 'warning') {
    if (!('Notification' in window)) return;

    if (Notification.permission === 'granted') {
      this.showToastNotification(title, body);
    } else if (Notification.permission !== 'denied' && !this.hasRequestedNotificationPermission) {
      this.hasRequestedNotificationPermission = true;
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.showToastNotification(title, body);
        }
      });
    }

    // Always play sound chime for alerts
    this.playAlertSound(severity);
  }

  private showToastNotification(title: string, body: string) {
    try {
      new Notification(title, {
        body: body,
        icon: 'assets/images/logo.png'
      });
    } catch (e) {
      console.warn('Browser desktop notification display error:', e);
    }
  }
}
